import { supabase } from './supabase';
import {
  fetchRoleBasedProjects,
  normalizeProfileId,
  normalizeUserRole,
} from './fetchRoleBasedProjects';
import { fetchPortalProfile } from './portalProfile';

export interface ProjectScope {
  visibleProjectIds: string[] | null;
  isFullAccess: boolean;
  loading: boolean;
  error: string | null;
}

let cachedScope: {
  userId: string;
  profileId: string | null;
  role: string | null;
  scope: ProjectScope;
} | null = null;

export interface GetProjectScopeOptions {
  profileId?: string;
  role?: string;
  userId?: string;
  userEmail?: string | null;
}

/**
 * Determine visible project IDs for the current authenticated user.
 * Uses the same Portal rules as fetchRoleBasedProjects / sidebar:
 *   - management => full access (visibleProjectIds = null)
 *   - subcontractor => jphph_project_members (member_role = subcontractor)
 *   - client => client_user_id OR jphph_project_members (member_role = client)
 */
export async function getProjectScope(options: GetProjectScopeOptions = {}): Promise<ProjectScope> {
  try {
    let profileId = normalizeProfileId(options.profileId);
    let role = normalizeUserRole(options.role);
    let authUserId = options.userId ?? null;

    if (!profileId || !role) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { visibleProjectIds: [], isFullAccess: false, loading: false, error: 'Not signed in' };
      }
      authUserId = user.id;

      const { profile, error: profileErr } = await fetchPortalProfile(user.id, user.email);
      if (profileErr) {
        return { visibleProjectIds: [], isFullAccess: false, loading: false, error: profileErr };
      }
      if (!profile || !profile.is_active) {
        return {
          visibleProjectIds: [],
          isFullAccess: false,
          loading: false,
          error: profile ? 'Account is inactive' : 'Profile not found for current user',
        };
      }

      profileId = normalizeProfileId(profile.id);
      role = normalizeUserRole(profile.user_type);
    }

    if (!profileId || !role) {
      return {
        visibleProjectIds: [],
        isFullAccess: false,
        loading: false,
        error: 'Missing profile or role for project access',
      };
    }

    if (cachedScope && cachedScope.userId === (authUserId || profileId) && cachedScope.profileId === profileId && cachedScope.role === role) {
      return cachedScope.scope;
    }

    if (role === 'management') {
      const result: ProjectScope = {
        visibleProjectIds: null,
        isFullAccess: true,
        loading: false,
        error: null,
      };
      cachedScope = { userId: authUserId || profileId, profileId, role, scope: result };
      return result;
    }

    const { rows, error } = await fetchRoleBasedProjects({
      columns: 'id',
      profileId,
      role,
    });

    if (error) {
      return { visibleProjectIds: [], isFullAccess: false, loading: false, error };
    }

    const ids = rows
      .map((row) => row.id as string)
      .filter((id): id is string => Boolean(id));

    const result: ProjectScope = {
      visibleProjectIds: ids,
      isFullAccess: false,
      loading: false,
      error: null,
    };

    cachedScope = { userId: authUserId || profileId, profileId, role, scope: result };
    return result;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[ProjectScope] error:', msg);
    return { visibleProjectIds: [], isFullAccess: false, loading: false, error: msg };
  }
}

/** Clear the cached scope. Useful after sign-out / sign-in change. */
export function clearProjectScopeCache() {
  cachedScope = null;
}

/** True when the user may open a project detail page for the given id. */
export function canAccessProject(projectId: string, scope: ProjectScope): boolean {
  if (scope.isFullAccess || scope.visibleProjectIds === null) return true;
  if (!scope.visibleProjectIds) return false;
  return scope.visibleProjectIds.includes(projectId);
}
